import "./Reorderable.css"

import { Children, useEffect, useRef } from "react"

function emitWindowFileDragEvent(dataTransfer) {
  var event = new CustomEvent("dragenter")
  event.dataTransfer = dataTransfer

  window.dispatchEvent(event)
}

function Reorderable({
  id,
  itemListClass,
  itemContainerClass,
  onMoveItem,
  children,
  disableDropzone,
  isDraggable = true,
}) {
  const lastItemOverId = useRef(null)
  const draggedItemWidth = useRef(null)
  const draggedItemHeight = useRef(null)
  const draggedItemIndex = useRef(null)
  const draggedItemId = useRef(null)

  const itemId = (index) => `${id ? `${id}-` : ""}item-${index}`
  const itemSetId = `${id ? `${id}-` : ""}item-set`

  useEffect(() => {
    if (!disableDropzone) return
    const tileDragged = () => {
      disableDropzone(true)
    }
    const tileDropped = () => {
      disableDropzone(false)
    }

    const itemSetNode = document.getElementById(itemSetId)

    itemSetNode.addEventListener("drag", tileDragged)
    itemSetNode.addEventListener("dragend", tileDropped)

    return () => [
      itemSetNode.removeEventListener("drag", tileDragged),
      itemSetNode.addEventListener("dragend", tileDropped),
    ]
  }, [itemSetId, disableDropzone])

  const showPlaceholder = (index) => {
    if (lastItemOverId.current) {
      const prevPlaceholder = document.getElementsByClassName("placeholder")
      if (prevPlaceholder) {
        for (const pph of prevPlaceholder) {
          pph.parentNode.removeChild(pph)
        }
      }
    }

    if (index !== null) {
      const placeholder = document.createElement("div")
      placeholder.id = `${itemId(index)}-placeholder`
      placeholder.className = itemContainerClass + " placeholder"
      placeholder.style.width = `${draggedItemWidth.current}px`
      placeholder.style.height = `${draggedItemHeight.current}px`
      placeholder.style.display = "inline-block"
      placeholder.style.background = "var(--neutral-9)"
      placeholder.style.boxShadow = "var(--box-shadow-well-1)"
      placeholder.addEventListener("dragover", placeholderDragOver)
      placeholder.addEventListener("drop", itemDropped(index))

      const itemAfter = document.getElementById(itemId(index))
      if (itemAfter) {
        itemAfter.parentNode.insertBefore(placeholder, itemAfter)
      } else {
        const itemSet = document.getElementById(itemSetId)
        itemSet.appendChild(placeholder)
      }

      lastItemOverId.current = itemId(index)
    }
  }

  const itemDragStart = (index) => {
    return (e) => {
      e.stopPropagation()

      draggedItemId.current = itemId(index)
      draggedItemIndex.current = index
      const draggedItem = document.getElementById(itemId(index))
      draggedItemHeight.current = draggedItem.offsetHeight
      draggedItemWidth.current = draggedItem.offsetWidth
      setTimeout(() => {
        const item = document.getElementById(itemId(index))
        item.style.display = "none"
        showPlaceholder(index)
      }, 1)
    }
  }

  const itemDragEnter = (index) => {
    return (e) => {
      e.stopPropagation()
      e.preventDefault()

      if (
        e.dataTransfer.types.indexOf("Files") !== -1 ||
        draggedItemId.current === null
      ) {
        return
      }

      if (lastItemOverId.current && lastItemOverId.current !== itemId(index)) {
        showPlaceholder(index)
      }

      lastItemOverId.current = itemId(index)
    }
  }

  const itemDragOver = (index) => {
    return (e) => {
      e.stopPropagation()
      e.preventDefault()

      if (draggedItemId.current === null) return

      const { dataTransfer } = e

      if (dataTransfer.types.indexOf("Files") !== -1) {
        emitWindowFileDragEvent(dataTransfer)
        return
      }

      if (lastItemOverId.current === itemId(index)) {
        showPlaceholder(index + 1)
      }
    }
  }

  const itemDragEnd = () => {
    if (draggedItemId.current) {
      cancelDrag()
      clearLastDraggedItem()
    }
  }

  const clearLastDraggedItem = () => {
    draggedItemId.current = null
    draggedItemIndex.current = null
    draggedItemHeight.current = null
    draggedItemWidth.current = null
  }

  const cancelDrag = () => {
    // restore item that was being dragged
    const item = document.getElementById(draggedItemId.current)
    item.style.display = ""

    // hide placeholder that was being shown
    showPlaceholder(null)
  }

  const placeholderDragOver = (e) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const itemDropped = (index) => {
    return (e) => {
      e.stopPropagation()
      e.preventDefault()

      cancelDrag()

      if (typeof onMoveItem !== "function") return

      if (draggedItemIndex.current !== index) {
        if (index < draggedItemIndex.current) {
          onMoveItem(draggedItemIndex.current, index)
        } else {
          if (index !== draggedItemIndex.current + 1) {
            onMoveItem(draggedItemIndex.current, index - 1)
          }
        }
      }
      clearLastDraggedItem()
    }
  }

  return (
    <div id={itemSetId} className={itemListClass || "reorderable"}>
      {Children.map(children, (item, n) => (
        <div
          className={itemContainerClass}
          id={itemId(n)}
          draggable={isDraggable}
          onDragStart={itemDragStart(n)}
          onDragEnd={itemDragEnd}
          onDragEnter={itemDragEnter(n)}
          onDragOver={itemDragOver(n)}
        >
          {item}
        </div>
      ))}
    </div>
  )
}

export default Reorderable
